import { auth } from './config';

// Claims an admin can toggle from the dashboard.
// Keep in sync with MANAGEABLE_CLAIMS in service/routes/users.js.
export const AVAILABLE_CLAIMS = [
  {
    key: 'member',
    label: 'Member',
    description: 'Baseline access required to call any backend service.',
  },
  {
    key: 'admin',
    label: 'Admin',
    description: 'Full access to the admin dashboard and user management (implies Member).',
  },
];

// Base URL of the Cloud Run service, set via REACT_APP_API_BASE_URL per
// environment (see .env.development and .env.production).
const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

async function authedFetch(path, options = {}) {
  const user = auth.currentUser;
  if (!user) throw new Error('You must be signed in.');
  const token = await user.getIdToken();

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data;
}

// Upcoming events from the shared Google Calendar. Server-side this is behind
// the `member` gate, so the caller must be signed in with the `member` claim.
export async function listCalendarEvents({ max } = {}) {
  const qs = max ? `?max=${encodeURIComponent(max)}` : '';
  const data = await authedFetch(`/calendar${qs}`);
  return Array.isArray(data.events) ? data.events : [];
}

// --- Community news ---

// Published (non-archived) news articles for the signed-in member, newest
// first. Server-side this is behind the `member` gate. `max` optionally caps
// the count (used by the home-page preview).
export async function listNews({ max } = {}) {
  const qs = max ? `?max=${encodeURIComponent(max)}` : '';
  const data = await authedFetch(`/news${qs}`);
  return Array.isArray(data.articles) ? data.articles : [];
}

// Admin: every article, including archived ones, for the management page.
export async function listAllNews() {
  const data = await authedFetch('/admin/news');
  return Array.isArray(data.articles) ? data.articles : [];
}

// Admin: create a news article. `date` is 'YYYY-MM-DD'; `content` is Markdown.
export async function createNews({ title, date, content }) {
  return authedFetch('/admin/news', {
    method: 'POST',
    body: JSON.stringify({ title, date, content }),
  });
}

// Admin: edit an existing article.
export async function updateNews(id, { title, date, content }) {
  return authedFetch(`/admin/news/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify({ title, date, content }),
  });
}

// Admin: archive an article (hide it from the member feed) or restore it.
export async function setNewsArchived(id, archived) {
  return authedFetch(`/admin/news/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
    body: JSON.stringify({ archived }),
  });
}

// Returns the array of users (admin only; rejects otherwise).
export async function listUsers() {
  const data = await authedFetch('/admin/users');
  return data.users;
}

// Persists the managed claims for a user. `claims` is { [key]: boolean }.
export async function setUserClaims(uid, claims) {
  return authedFetch(`/admin/users/${encodeURIComponent(uid)}/claims`, {
    method: 'POST',
    body: JSON.stringify({ claims }),
  });
}

// Enables or disables a user account. Passing `false` re-enables a rejected
// (disabled) account so the person can sign in again.
export async function setUserDisabled(uid, disabled) {
  return authedFetch(`/admin/users/${encodeURIComponent(uid)}/disabled`, {
    method: 'POST',
    body: JSON.stringify({ disabled }),
  });
}

// Called by a freshly signed-up (unapproved) user: records a pending request
// and notifies admins by email. Idempotent + read-exempt on the server.
export async function requestAccess() {
  return authedFetch('/signup-request', { method: 'POST', body: '{}' });
}

// --- Admin: signup approval requests ---
export async function listSignupRequests(status = 'pending') {
  const data = await authedFetch(`/admin/signup-requests?status=${encodeURIComponent(status)}`);
  return data.requests;
}

export async function getSignupRequest(id) {
  return authedFetch(`/admin/signup-requests/${encodeURIComponent(id)}`);
}

export async function approveSignupRequest(id) {
  return authedFetch(`/admin/signup-requests/${encodeURIComponent(id)}/approve`, { method: 'POST', body: '{}' });
}

export async function rejectSignupRequest(id) {
  return authedFetch(`/admin/signup-requests/${encodeURIComponent(id)}/reject`, { method: 'POST', body: '{}' });
}
